<template>
  <component :is="tag" :class="['product-title', { white }]">
    <span
      v-if="brandDisplayName && !isSTC"
      class="brand margin-bottom-xxs"
      :class="isMcom ? 'large' : 'subtitle-1'"
      itemprop="brand"
      itemtype="https://schema.org/Brand"
      itemscope
    >
      <a class="link" :href="brandUrl" itemprop="url" @click="tagProductTitleClick" v-html="brandDisplayName"></a>
      <meta itemprop="name" content="{{ brandDisplayName }}" />
    </span>
    <a
      v-if="productUrl && titleText"
      :href="productUrl"
      :target="allowOpeningInNewTab ? '_blank' : '_self'"
      class="title"
      :class="titleClass"
      itemprop="name"
      @click="$emit('click', $event)"
      v-html="titleText"
    ></a>
    <span v-else-if="titleText" class="title" :class="titleClass" itemprop="name" v-html="titleText"></span>
  </component>
  <span v-if="showExclusiveText" :class="['exclusive-text', { white }]">
    {{ exclusiveText }}
  </span>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { useBrand } from '~/composables/useBrand';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import tagProductTitleClick from '~/features/analytics/productTitle';

const { isMcom } = useBrand();
const { isChanelFlag, isExclusiveBrandFlag } = useProductFlags();

const isSTC = inject('isSTC', false);

interface Props {
  productName: string;
  productExclusiveName?: string;
  pdfEmailDescription: string;
  description?: string;
  /** Brand display name */
  brandDisplayName?: string;
  /**
   * Brand url
   * Will not be rendered if `brandDisplayName` is not provided
   */
  brandUrl?: string;
  productUrl?: string;
  tag?: keyof HTMLElementTagNameMap;
  allowOpeningInNewTab?: boolean;
  white?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  productExclusiveName: '',
  description: undefined,
  brandDisplayName: undefined,
  brandUrl: undefined,
  productUrl: '',
  tag: 'h1',
  allowOpeningInNewTab: false,
  white: false,
});

const showExclusiveText = isMcom && isExclusiveBrandFlag;

const exclusiveText = "Macy's Exclusive";

const titleText = computed(() => {
  if (isMcom) {
    let titleArr = [];
    if (showExclusiveText && props.productExclusiveName) {
      titleArr = [props.description, props.productExclusiveName];
    } else {
      titleArr = [props.description, props.productName];
    }
    return titleArr.filter(Boolean).join('<br>');
  }
  const bcomProductName = isChanelFlag ? props.productName : props.pdfEmailDescription;
  return [bcomProductName, props.description].filter(Boolean).join('<br>');
});

const titleClass = computed(() => {
  if (isSTC) {
    return isMcom ? 'brand-link-lg' : 'product-detail-link-lg';
  }
  return isMcom ? 'body' : 'subtitle-2';
});

defineEmits<(e: 'click', value: MouseEvent | KeyboardEvent) => void>();
</script>






<style lang="scss" scoped>
@import '@core/csg/src/scss/typography/typography-mixins.scss';

.link:not(:hover):not(:active):not(:focus) {
  text-decoration: none;
}

.subtitle-1 {
  text-decoration: none;
}

.product-title {
  display: flex;
  flex-direction: column;

  .title {
    color: $black;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &.white * {
    color: $white;
  }
}


.subtitle-1 {
  color: $black;
  font-weight: $font-weight-heavy;
  font-size: rem-calc(22);
  line-height: rem-calc(28);
  letter-spacing: $body-letter-spacing;
  text-transform: $global-text-transform;

  @include breakpoint(medium down) {
    font-size: rem-calc(20);
    line-height: rem-calc(25);
  }
}

.subtitle-2 {
  color: $black;
  font-weight: 500;
  font-size: rem-calc(18);
  line-height: rem-calc(22);
  letter-spacing: 0em;
  text-transform: $global-text-transform;

  @include breakpoint(medium down) {
    font-size: $font-size-lg;
    line-height: $line-height-lg;
  }
}
</style>